import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useCreateAuction, useUpdateAuction } from './hooks/useAuctions';
import { useAuctionHouses } from './hooks/useAuctionHouses';
import { contacts } from '../../utils/api';

export default function AuctionFormModal({ isOpen, onClose, isEdit, initialData, onDelete }) {
  const { mutate: createAuction, isLoading: isCreating } = useCreateAuction();
  const { mutate: updateAuction, isLoading: isUpdating } = useUpdateAuction();
  const { data: auctionHouses, isLoading: isLoadingHouses, refetch: refetchAuctionHouses } = useAuctionHouses();
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [error, setError] = useState('');
  const isLoading = isCreating || isUpdating;

  const [formData, setFormData] = useState(
    initialData || {
      auction_house_id: '',
      name: '',
      start_date: new Date().toISOString().split('T')[0], // Use YYYY-MM-DD format for date inputs
      end_date: '',
      daily_times: {},
      auction_house_currency: 'USD',
      user_currency: 'USD',
      buyers_premium: '',
      profit_margin: '',
      source_url: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }
  );

  useEffect(() => {
    if (initialData) {
      const formatDateForInput = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toISOString().split('T')[0];
      };

      setFormData({
        ...initialData,
        start_date: formatDateForInput(initialData.start_date),
        end_date: formatDateForInput(initialData.end_date),
      });
    }
  }, [initialData]);

  // Generate array of dates between start and end date
  const getDatesInRange = (startDate, endDate) => {
    const dates = [];
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date(startDate);
    
    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      dates.push(date.toISOString().split('T')[0]); // Use YYYY-MM-DD format for date inputs
    }
    
    return dates;
  };

  // Get dates array based on current form data
  const dateRange = getDatesInRange(formData.start_date, formData.end_date);

  const handleSubmit = (event) => {
    event.preventDefault();
    
    // Ensure we have a time for the start date
    if (!formData.daily_times[formData.start_date]) {
      setError('Start time is required');
      return;
    }

    const auctionData = {
      ...formData,
      auction_house_id: parseInt(formData.auction_house_id),
      buyers_premium: parseFloat(formData.buyers_premium || '0'),
      profit_margin: parseFloat(formData.profit_margin || '0'),
      end_date: formData.end_date || null,
      daily_times: {
        [formData.start_date]: formData.daily_times[formData.start_date],
        ...(formData.end_date ? Object.fromEntries(
          Object.entries(formData.daily_times)
            .filter(([date, time]) => date !== formData.start_date && time)
        ) : {})
      }
    };

    setError('');

    if (isEdit) {
      const { lots, ...restOfAuctionData } = auctionData;
      const { lots: initialLots, ...restOfInitialData } = initialData;
      updateAuction({
        ...restOfInitialData,
        ...restOfAuctionData,
        id: initialData.id
      }, {
        onSuccess: () => {
          onClose();
        },
        onError: (err) => {
          console.error("Failed to update auction:", err);
          setError(err.message || 'An unexpected error occurred');
        }
      });
    } else {
      createAuction(auctionData, {
        onSuccess: () => {
          // Reset form data to initial state
          setFormData({
            auction_house_id: '',
            name: '',
            start_date: new Date().toISOString().split('T')[0], // Use YYYY-MM-DD format for date inputs
            end_date: '',
            daily_times: {},
            auction_house_currency: 'USD',
            user_currency: 'USD',
            buyers_premium: '',
            profit_margin: '',
            source_url: '',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          });
          onClose();
        }
      });
    }
  };

  return (
    <>
      <Transition.Root show={isOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                  <button
                    type="button"
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                    {isEdit ? 'Edit Auction Details' : 'Add New Auction'}
                  </Dialog.Title>

                  <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                    <p className="text-sm text-gray-500">Fields marked with an asterisk (*) are required.</p>

                    <div className="w-1/2">
                      <label htmlFor="auction_house_id" className="block text-sm font-medium text-gray-700">
                        Auction House *
                      </label>
                      <select
                        id="auction_house_id"
                        name="auction_house_id"
                        required
                        value={formData.auction_house_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, auction_house_id: e.target.value }))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="">Select an auction house</option>
                        {auctionHouses?.map(house => (
                          <option key={house.id} value={house.id}>
                            {house.company_name}
                          </option>
                        ))}
                        {(!auctionHouses || auctionHouses.length === 0) && (
                          <option value="" disabled>No auction houses available</option>
                        )}
                      </select>
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={() => setShowNewContactForm(true)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          + Add New Auction House
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                          Auction Name/Number *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          autoComplete="off"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label htmlFor="source_url" className="block text-sm font-medium text-gray-700">
                          Auction URL
                        </label>
                        <input
                          type="url"
                          id="source_url"
                          name="source_url"
                          value={formData.source_url}
                          onChange={(e) => setFormData(prev => ({ ...prev, source_url: e.target.value }))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="https://"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
                          Start Date *
                        </label>
                        <input
                          type="date"
                          id="start_date"
                          name="start_date"
                          required
                          value={formData.start_date}
                          onChange={(e) => {
                            const newStartDate = e.target.value;
                            setFormData(prev => ({
                              ...prev,
                              start_date: newStartDate,
                              // Clear end date if it's before new start date
                              end_date: prev.end_date && new Date(prev.end_date) < new Date(newStartDate).toISOString() ? '' : prev.end_date,
                              // Reset times for dates no longer in range
                              daily_times: {}
                            }));
                          }}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
                          End Date
                        </label>
                        <input
                          type="date"
                          id="end_date"
                          name="end_date"
                          value={formData.end_date}
                          min={formData.start_date}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              end_date: e.target.value,
                              // Reset times for dates no longer in range
                              daily_times: {}
                            }));
                          }}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          Optional. Leave empty for single-day auctions.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="auction_time" className="block text-sm font-medium text-gray-700">
                        Auction Times *
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        {dateRange.map(date => (
                          <div key={date} className="flex items-center space-x-2">
                            <span className="text-sm text-gray-500 w-32">
                              {date.split('-').slice(1).concat(date.split('-')[0]).join('/')}
                            </span>
                            <input
                              type="time"
                              id="auction_time"
                              name="auction_time"
                              required
                              value={formData.daily_times[date] || ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                daily_times: {
                                  ...prev.daily_times,
                                  [date]: e.target.value
                                }
                              }))}
                              className="block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-gray-500">
                        Enter times in your local timezone ({formData.timezone})
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="auction_house_currency" className="block text-sm font-medium text-gray-700">
                          Auction House Currency *
                        </label>
                        <select
                          id="auction_house_currency"
                          name="auction_house_currency"
                          required
                          value={formData.auction_house_currency}
                          onChange={(e) => setFormData(prev => ({ ...prev, auction_house_currency: e.target.value }))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                          <option value="CAD">CAD</option>
                          <option value="AUD">AUD</option>
                          <option value="CHF">CHF</option>
                          <option value="JPY">JPY</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="user_currency" className="block text-sm font-medium text-gray-700">
                          Your Currency *
                        </label>
                        <select
                          id="user_currency"
                          name="user_currency"
                          required
                          value={formData.user_currency}
                          onChange={(e) => setFormData(prev => ({ ...prev, user_currency: e.target.value }))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                          <option value="CAD">CAD</option>
                          <option value="AUD">AUD</option>
                          <option value="CHF">CHF</option>
                          <option value="JPY">JPY</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="buyers_premium" className="block text-sm font-medium text-gray-700">
                          Buyer's Premium (%)
                        </label>
                        <input
                          type="number"
                          id="buyers_premium"
                          name="buyers_premium"
                          min="0"
                          step="0.01"
                          value={formData.buyers_premium}
                          onChange={(e) => setFormData(prev => ({ ...prev, buyers_premium: e.target.value }))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          Leave empty to default to 0%
                        </p>
                      </div>

                      <div>
                        <label htmlFor="profit_margin" className="block text-sm font-medium text-gray-700">
                          Profit Margin (%)
                        </label>
                        <input
                          type="number"
                          id="profit_margin"
                          name="profit_margin"
                          min="0"
                          step="0.01"
                          value={formData.profit_margin}
                          onChange={(e) => setFormData(prev => ({ ...prev, profit_margin: e.target.value }))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          Leave empty to default to 0%
                        </p>
                      </div>
                    </div>

                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-3 sm:gap-3">
                      {isEdit && (
                        <button
                          type="button"
                          onClick={onDelete}
                          className="inline-flex w-full justify-center items-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:col-start-1"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="-ml-1 mr-2 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Delete
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-2"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-3 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? (isEdit ? 'Saving...' : 'Adding...') : (isEdit ? 'Save Changes' : 'Add Auction')}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <Transition.Root show={showNewContactForm} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowNewContactForm(false)}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="flex justify-between items-center mb-4">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                      New Auction House
                    </Dialog.Title>
                    <button
                      type="button"
                      onClick={() => setShowNewContactForm(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <span className="sr-only">Close</span>
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {error && (
                    <div className="rounded-md bg-red-50 p-4 mb-4">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    
                    try {
                      const newContact = await contacts.create({
                        company_name: formData.get('company_name'),
                        email: formData.get('email'),
                        phone: formData.get('phone'),
                        address: formData.get('address'),
                        website: formData.get('website'),
                        contact_type: 'auction_house'
                      });
                      
                      setShowNewContactForm(false);
                      // Refresh the auction houses list and select the new one
                      await refetchAuctionHouses();
                      setFormData(prev => ({ ...prev, auction_house_id: newContact.id }));
                    } catch (err) {
                      setError(err.message || 'Failed to create auction house');
                    }
                  }} className="space-y-4">
                    <div>
                      <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        name="company_name"
                        id="company_name"
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        id="phone"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                        Website
                      </label>
                      <input
                        type="url"
                        name="website"
                        id="website"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                        Address
                      </label>
                      <textarea
                        name="address"
                        id="address"
                        rows={3}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>

                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                      <button
                        type="submit"
                        className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
                      >
                        Create Auction House
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowNewContactForm(false)}
                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
